import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

// FHA Loan: 3.5% min down with 580+ credit. UFMIP 1.75% financed into loan.
// Annual MIP 0.55% (for loans ≤ $726,200 @ <90% LTV) paid monthly.
const FHA_BASELINE_LIMIT = 498257;

export default function FhaLoanPage() {
    const [homePrice, setHomePrice] = useState(350000);
    const [downPct, setDownPct] = useState(3.5);
    const [rate, setRate] = useState(6.5);
    const [years, setYears] = useState(30);
    const [ufmipPct, setUfmipPct] = useState(1.75);
    const [annualMipPct, setAnnualMipPct] = useState(0.55);
    const [propertyTax, setPropertyTax] = useState(1.1);
    const [insurance, setInsurance] = useState(1500);

    const result = useMemo(() => {
        const hp = toNum(homePrice);
        const dp = toNum(downPct);
        const r = toNum(rate);
        const y = Math.max(1, toNum(years));
        const down = (hp * dp) / 100;
        const baseLoan = Math.max(0, hp - down);
        const ufmip = (baseLoan * toNum(ufmipPct)) / 100;
        const loan = baseLoan + ufmip; // UFMIP is typically financed into loan
        const pi = monthlyPayment(loan, r, y);
        const monthlyMip = (loan * (toNum(annualMipPct) / 100)) / 12;
        const tax = (hp * (toNum(propertyTax) / 100)) / 12;
        const ins = toNum(insurance) / 12;
        const monthly = pi + monthlyMip + tax + ins;
        const overLimit = baseLoan > FHA_BASELINE_LIMIT;
        return { monthly, pi, monthlyMip, tax, ins, down, ufmip, loan, baseLoan, totalPaid: (pi + monthlyMip) * y * 12, overLimit };
    }, [homePrice, downPct, rate, years, ufmipPct, annualMipPct, propertyTax, insurance]);

    return (
        <CalculatorShell
            slug="fha-loan"
            explainer={
                     <>
                    <h2>How this FHA loan calculator works</h2>
                    <p>
                        An FHA loan calculator estimates your monthly mortgage payment including both the
                        upfront and annual mortgage insurance premiums (MIP) specific to FHA loans. It's
                        particularly relevant for first-time buyers or those with lower credit scores who may
                        not qualify for conventional financing.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Minimum down payment:</strong> 3.5% for credit scores of 580 or higher; 10% for scores between 500 and 579.</li>
                        <li><strong>Upfront MIP:</strong> 1.75% of the loan amount, typically rolled into the loan balance at closing.</li>
                        <li><strong>Annual MIP:</strong> 0.55–1.05% of the loan amount divided into monthly payments — added to every mortgage payment.</li>
                        <li><strong>FHA loan limits:</strong> vary by county and are updated annually; the calculator uses standard limits by default.</li>
                        <li><strong>Monthly payment breakdown:</strong> principal &amp; interest plus annual MIP divided by 12, plus taxes and homeowners insurance.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Unlike conventional PMI, FHA MIP doesn't automatically cancel — once you reach 20% equity, refinance to a conventional loan to eliminate it.</li>
                        <li>FHA property condition standards are stricter than conventional; sellers may resist FHA offers on older or as-is properties.</li>
                        <li>If you already have an FHA loan, the FHA Streamline Refinance requires minimal paperwork and no appraisal in most cases.</li>
                    </ul>
                </>
            }
        >
            {result.overLimit && (
                <div className="mb-6 bg-accent/10 border border-accent/30 rounded-lg p-4 text-sm text-primary" data-testid="fha-limit-warning">
                    <strong>Heads up:</strong> your base loan amount ({fmtUSD(result.baseLoan, { decimals: 0 })}) exceeds
                    the FHA baseline limit of {fmtUSD(FHA_BASELINE_LIMIT, { decimals: 0 })}. Check the FHA limit for
                    your specific county — high-cost areas go up to $1.14M, but most counties do not.
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Home price" prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Down payment" suffix="%" value={downPct} onChange={setDownPct} step={0.5} dataTestId="calc-input-down" hint="FHA min: 3.5%" />
                        <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Loan term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                        <Field label="UFMIP" suffix="%" value={ufmipPct} onChange={setUfmipPct} step={0.05} dataTestId="calc-input-ufmip" hint="1.75% typical" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Annual MIP" suffix="%" value={annualMipPct} onChange={setAnnualMipPct} step={0.05} dataTestId="calc-input-mip" hint="0.55% typical" />
                        <Field label="Property tax" suffix="%" value={propertyTax} onChange={setPropertyTax} step={0.05} dataTestId="calc-input-tax" />
                    </div>
                    <Field label="Annual home insurance" prefix="$" value={insurance} onChange={setInsurance} dataTestId="calc-input-ins" />
                    <div className="pt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono tabular">
                        <span>Down: {fmtUSD(result.down)}</span>
                        <span>UFMIP: {fmtUSD(result.ufmip)}</span>
                        <span>Loan: {fmtUSD(result.loan)}</span>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Monthly payment"
                        value={result.monthly}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "P&I", value: result.pi, format: (n) => fmtUSD(n), testId: "calc-result-pi" },
                            { label: "Monthly MIP", value: result.monthlyMip, format: (n) => fmtUSD(n), testId: "calc-result-mip" },
                            { label: "Tax + Ins", value: result.tax + result.ins, format: (n) => fmtUSD(n), testId: "calc-result-escrow" },
                            { label: "Total paid", value: result.totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
